import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRouter,
  parseSearchWith,
  RouterProvider,
  stringifySearchWith,
} from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AnalyticsIdentityMount } from './components/analytics-identity-mount.js';
import { BoundaryError, NotFoundError } from './components/error-states.js';
import { DashboardI18nProvider } from './components/i18n-provider.js';
import { NotificationStreamMount } from './components/notification-stream-mount.js';
import { PageLoader } from './components/page-loader.js';
import { Toaster } from './components/toaster.js';
import { bootstrapI18nSync } from './lib/i18n.js';
import { routeTree } from './routeTree.gen.js';
import './styles/index.css';

// Lingui throws if a component renders with no active locale, so put it
// into a usable state before React mounts. The real catalog swaps in from
// DashboardI18nProvider once it loads.
bootstrapI18nSync();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Defaults replace TanStack Router's built-in red "Something went wrong!"
// boxes with the magig design-system error states (handoff §5.1 / §5.4).
// Per-route components can still override these when local handling fits
// (e.g. an inline section-level retry card).
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Only show the full-screen loader if the navigation actually stalls —
  // fast navigations (warm cache, sub-150ms) skip it entirely so the UI
  // doesn't flash. Once shown, keep it up for 300ms minimum so it never
  // blinks. The top progress bar (RouterProgress, mounted in __root.tsx)
  // covers everything below this threshold for continuous transition
  // feedback.
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultPendingComponent: PageLoader,
  defaultErrorComponent: ({ error, reset }) => <BoundaryError error={error} reset={reset} />,
  defaultNotFoundComponent: () => <NotFoundError />,
  // Clean URLs. TanStack Router's defaults JSON-encode every search
  // value, which surfaces as `?onboarding="1"` (literal quotes) in the
  // address bar — ugly and confusing. We wrap the codec so plain string
  // primitives stay as `?onboarding=1`, while still routing arrays /
  // objects through JSON so anything richer than a string keeps working.
  stringifySearch: stringifySearchWith((value) =>
    typeof value === 'string' ? value : JSON.stringify(value),
  ),
  parseSearch: parseSearchWith((raw) => {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('#root element missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <DashboardI18nProvider>
        <RouterProvider router={router} />
        <Toaster />
        <NotificationStreamMount />
        <AnalyticsIdentityMount />
      </DashboardI18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
